import { formatEmojiToken, getEmojiImageUrl, parseEmojiContent } from '@/constants/emoji';
export const RICH_EMOJI_CLASS = 'rich-emoji';
const EMOJI_TOKEN_IN_TEXT_RE = /\[emoji_\d+\]/g;
export function isRichEmojiNode(node) {
    return (!!node &&
        node.nodeType === Node.ELEMENT_NODE &&
        node.tagName === 'IMG' &&
        !!node.dataset.emoji);
}
export function createEmojiImg(data) {
    const img = document.createElement('img');
    img.className = RICH_EMOJI_CLASS;
    img.src = getEmojiImageUrl(data);
    img.alt = '表情';
    img.draggable = false;
    img.dataset.emoji = data;
    img.contentEditable = 'false';
    return img;
}
export function serializeEditor(root) {
    let result = '';
    root.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent ?? '';
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE)
            return;
        const el = node;
        if (isRichEmojiNode(el)) {
            result += formatEmojiToken(el.dataset.emoji ?? '');
            return;
        }
        if (el.tagName === 'BR') {
            result += '\n';
            return;
        }
        result += serializeEditor(el);
    });
    return result;
}
export function renderPlainTextToEditor(root, text) {
    root.innerHTML = '';
    if (!text)
        return;
    for (const part of parseEmojiContent(text)) {
        if (part.type === 'text') {
            root.appendChild(document.createTextNode(part.value));
        }
        else {
            root.appendChild(createEmojiImg(part.value));
        }
    }
}
function insertNodeAtSelection(node) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0)
        return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
}
function isRangeInRoot(root, range) {
    return root.contains(range.commonAncestorContainer);
}
function insertNodeAtRange(range, node) {
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    return range.cloneRange();
}
/** 在编辑器内容末尾创建光标 Range */
export function createRangeAtEditorEnd(root) {
    const range = document.createRange();
    if (!root.childNodes.length) {
        range.setStart(root, 0);
        range.collapse(true);
        return range;
    }
    const last = root.lastChild;
    if (last.nodeType === Node.TEXT_NODE) {
        range.setStart(last, last.textContent?.length ?? 0);
    }
    else {
        range.setStartAfter(last);
    }
    range.collapse(true);
    return range;
}
export function setEditorSelection(root, range) {
    const selection = window.getSelection();
    if (!selection)
        return;
    const target = range && isRangeInRoot(root, range) ? range.cloneRange() : createRangeAtEditorEnd(root);
    try {
        selection.removeAllRanges();
        selection.addRange(target);
    }
    catch {
        // 部分移动端 WebView 在未聚焦时 addRange 可能失败
    }
}
export function saveEditorRangeAtEnd(root) {
    return createRangeAtEditorEnd(root);
}
export function insertEmojiAtCursor(root, data) {
    root.focus();
    insertNodeAtSelection(createEmojiImg(data));
}
/** 插入表情但不聚焦输入框，避免移动端弹出键盘 */
export function insertEmojiWithoutFocus(root, data, savedRange) {
    const img = createEmojiImg(data);
    if (savedRange && isRangeInRoot(root, savedRange)) {
        return insertNodeAtRange(savedRange.cloneRange(), img);
    }
    root.appendChild(img);
    const range = document.createRange();
    range.setStartAfter(img);
    range.collapse(true);
    return range;
}
/** 将文本节点中的 [emoji_N] 转为图片节点 */
export function normalizeEmojiTokensInEditor(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let current = walker.nextNode();
    while (current) {
        textNodes.push(current);
        current = walker.nextNode();
    }
    for (const textNode of textNodes) {
        const text = textNode.textContent ?? '';
        if (!EMOJI_TOKEN_IN_TEXT_RE.test(text))
            continue;
        EMOJI_TOKEN_IN_TEXT_RE.lastIndex = 0;
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        const re = new RegExp(EMOJI_TOKEN_IN_TEXT_RE.source, 'g');
        while ((match = re.exec(text)) !== null) {
            if (match.index > lastIndex) {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
            }
            const key = match[0].slice(1, -1);
            if (getEmojiImageUrl(key)) {
                fragment.appendChild(createEmojiImg(key));
            }
            else {
                fragment.appendChild(document.createTextNode(match[0]));
            }
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        textNode.parentNode?.replaceChild(fragment, textNode);
    }
}
function getEmojiBeforeRange(range) {
    const { startContainer, startOffset } = range;
    if (startContainer.nodeType === Node.TEXT_NODE) {
        const text = startContainer.textContent ?? '';
        const before = text.slice(0, startOffset);
        const tokenMatch = before.match(/(\[emoji_\d+\])$/);
        if (tokenMatch) {
            return { type: 'text-token', node: startContainer, token: tokenMatch[1] };
        }
        if (startOffset === 0) {
            const prev = startContainer.previousSibling;
            if (isRichEmojiNode(prev))
                return { type: 'img', node: prev };
        }
        return null;
    }
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
        if (startOffset > 0) {
            const prev = startContainer.childNodes[startOffset - 1];
            if (isRichEmojiNode(prev))
                return { type: 'img', node: prev };
        }
    }
    return null;
}
function getEmojiAfterRange(range) {
    const { startContainer, startOffset } = range;
    if (startContainer.nodeType === Node.TEXT_NODE) {
        const text = startContainer.textContent ?? '';
        const after = text.slice(startOffset);
        const tokenMatch = after.match(/^(\[emoji_\d+\])/);
        if (tokenMatch) {
            return { type: 'text-token', node: startContainer, token: tokenMatch[1] };
        }
        if (startOffset === text.length) {
            const next = startContainer.nextSibling;
            if (isRichEmojiNode(next))
                return { type: 'img', node: next };
        }
        return null;
    }
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
        const next = startContainer.childNodes[startOffset];
        if (isRichEmojiNode(next))
            return { type: 'img', node: next };
    }
    return null;
}
function deleteTextToken(textNode, token, offset, direction) {
    const text = textNode.textContent ?? '';
    if (direction === 'before') {
        const start = offset - token.length;
        textNode.textContent = text.slice(0, start) + text.slice(offset);
        return start;
    }
    textNode.textContent = text.slice(0, offset) + text.slice(offset + token.length);
    return offset;
}
function placeCursor(node, offset) {
    const selection = window.getSelection();
    if (!selection)
        return;
    const range = document.createRange();
    range.setStart(node, offset);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
}
export function handleRichInputKeydown(event, root) {
    if (event.key !== 'Backspace' && event.key !== 'Delete')
        return false;
    if (!root.contains(document.activeElement))
        return false;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed)
        return false;
    const range = selection.getRangeAt(0);
    if (event.key === 'Backspace') {
        const target = getEmojiBeforeRange(range);
        if (!target)
            return false;
        event.preventDefault();
        if (target.type === 'img') {
            const next = target.node.nextSibling;
            target.node.remove();
            if (next)
                placeCursor(next, 0);
            else
                placeCursor(root, root.childNodes.length);
        }
        else {
            const offset = range.startOffset;
            const newOffset = deleteTextToken(target.node, target.token, offset, 'before');
            placeCursor(target.node, newOffset);
        }
        return true;
    }
    const target = getEmojiAfterRange(range);
    if (!target)
        return false;
    event.preventDefault();
    if (target.type === 'img') {
        target.node.remove();
        placeCursor(range.startContainer, range.startOffset);
    }
    else {
        deleteTextToken(target.node, target.token, range.startOffset, 'after');
        placeCursor(target.node, range.startOffset);
    }
    return true;
}
export function insertPlainTextToEditor(root, text) {
    if (!text)
        return;
    root.focus();
    const selection = window.getSelection();
    if (!selection)
        return;
    let range = null;
    if (selection.rangeCount > 0) {
        const current = selection.getRangeAt(0);
        if (isRangeInRoot(root, current)) {
            range = current;
            range.deleteContents();
        }
    }
    if (!range) {
        range = createRangeAtEditorEnd(root);
    }
    const fragment = document.createDocumentFragment();
    for (const part of parseEmojiContent(text)) {
        if (part.type === 'text') {
            fragment.appendChild(document.createTextNode(part.value));
        }
        else {
            fragment.appendChild(createEmojiImg(part.value));
        }
    }
    range.insertNode(fragment);
    range.collapse(false);
    setEditorSelection(root, range);
    normalizeEmojiTokensInEditor(root);
}
export async function readClipboardPlainText() {
    try {
        if (navigator.clipboard?.readText) {
            return await navigator.clipboard.readText();
        }
    }
    catch {
        // ignore clipboard permission errors
    }
    return '';
}
export function handleRichInputPaste(event, root) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text)
        return;
    insertPlainTextToEditor(root, text);
}
